import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LoginPage } from "./pages/LoginPage";
import { AccountStatusPage } from "./pages/AccountStatusPage";
import { AccountSettingsPage } from "./pages/AccountSettingsPage";
import { ReportPage } from "./pages/ReportPage";
import { CatalogPage } from "./pages/CatalogPage";
import { MarkdownPage } from "./components/MarkdownPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { BlogIndexPage } from "./pages/blog/BlogIndexPage";
import { BlogArticlePage } from "./pages/blog/BlogArticlePage";
import { SeoHead } from "./components/SeoHead";
import { LogoMark } from "./LogoMark";
import { apiClient, API_BASE_URL } from "../data";
import { analytics } from "../analytics";
import "./App.css";

const TG_CHAT = "https://t.me/+Yh772q3r-jI5Yzg6";

// В production-билде react-snap пре-рендерит на localhost — noindex туда не пишем.
const isDevHost = process.env.NODE_ENV === "production"
  ? window.location.hostname.includes("dev.trend.nasonov.fun")
  : ["dev.trend.nasonov.fun", "localhost"].some(d => window.location.hostname.includes(d));

const GlobalNoindex: React.FC = () => {
  React.useEffect(() => {
    if (!isDevHost) return;
    let el = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      document.head.appendChild(el);
    }
    el.setAttribute("content", "noindex,nofollow");
  }, []);
  return null;
};

const SunIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5"/>
    <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
    <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
  </svg>
);

const MoonIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
);

// Корень — это лендинг для гостя и отчёт для авторизованного (см. RootRoute).
// На лендинге у приложения свой бета-бар и навигация — глобальные скрываем.
const isGuestLanding = (pathname: string) =>
  pathname === "/" && !localStorage.getItem("access_token");

const BetaBanner: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const hideOn = ["/login", "/auth/callback", "/api/auth/meta/callback", "/api/auth/instagram/callback"];
  if (isGuestLanding(location.pathname) || hideOn.some(p => location.pathname.startsWith(p))) return null;
  return (
    <div className="app-beta">
      <span className="app-beta__text">{t("login.betaBannerText")}</span>
      <a href={TG_CHAT} target="_blank" rel="noopener noreferrer" className="app-beta__link">
        {t("login.betaBannerLink")}
      </a>
    </div>
  );
};

interface HeaderProps {
  isDark: boolean;
  onToggleTheme: () => void;
}

const Header: React.FC<HeaderProps> = ({ isDark, onToggleTheme }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const isAuthenticated = !!localStorage.getItem("access_token");
  const hideOn = ["/login", "/auth/callback", "/api/auth/meta/callback", "/api/auth/instagram/callback", "/privacy", "/terms", "/data-deletion"];

  if (isGuestLanding(location.pathname) || hideOn.some(p => location.pathname.startsWith(p))) return null;

  const handleLogout = () => {
    analytics.logout();
    localStorage.removeItem("access_token");
    localStorage.removeItem("auth_method");
    localStorage.removeItem("report_data");
    localStorage.removeItem("oauth_state");
    navigate("/");
  };

  const navLink = (to: string, label: string) => {
    const active = location.pathname === to || (to === "/report" && location.pathname.startsWith("/report"));
    return (
      <button
        className={`app-header__link${active ? " app-header__link--active" : ""}`}
        onClick={() => navigate(to)}
      >
        {label}
      </button>
    );
  };

  return (
    <header className="app-header">
      <span
        className="app-header__logo"
        onClick={() => navigate(isAuthenticated ? "/report" : "/")}
      >
        <LogoMark size={26} />
        Trend
      </span>

      <nav className="app-header__nav">
        {navLink("/catalog", "Каталог")}
        {isAuthenticated && navLink("/settings", "Настройки")}

        <div className="app-header__sep" aria-hidden="true" />

        <button
          className="btn-theme"
          onClick={onToggleTheme}
          title={isDark ? "Светлая тема" : "Тёмная тема"}
        >
          {isDark ? <SunIcon /> : <MoonIcon />}
        </button>

        <button
          className="btn-lang"
          onClick={() => i18n.changeLanguage(i18n.language.startsWith("en") ? "ru" : "en")}
        >
          {i18n.language.startsWith("en") ? "RU" : "EN"}
        </button>

        {isAuthenticated
          ? <button className="btn-logout" onClick={handleLogout}>{t("header.logout")}</button>
          : <button className="app-header__link" onClick={() => navigate("/")}>Войти</button>
        }
      </nav>
    </header>
  );
};

// Корень без редиректов: гость видит лендинг, авторизованный — свой отчёт.
// URL остаётся "/" — нет прыжка на /login при старте, и canonical лендинга
// совпадает с sitemap (робот видит лендинг на /, /login закрыт в robots.txt).
interface RootRouteProps {
  isDark: boolean;
  onToggleTheme: () => void;
}

const RootRoute: React.FC<RootRouteProps> = ({ isDark, onToggleTheme }) => {
  const isAuthenticated = !!localStorage.getItem("access_token");
  return isAuthenticated
    ? <ReportPage key="my-report" />
    : <LoginPage isDark={isDark} onToggleTheme={onToggleTheme} />;
};

export const App: React.FC = () => {
  const [isDark, setIsDark] = React.useState(() => {
    const stored = localStorage.getItem("theme");
    if (stored === "dark" || stored === "light") return stored === "dark";
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  React.useEffect(() => {
    document.documentElement.setAttribute("data-theme", isDark ? "dark" : "light");
  }, [isDark]);

  // Follow system theme changes unless user has set a manual preference
  React.useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => {
      if (!localStorage.getItem("theme")) {
        setIsDark(e.matches);
      }
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const toggleTheme = () => {
    setIsDark(d => {
      const next = !d;
      localStorage.setItem("theme", next ? "dark" : "light");
      return next;
    });
  };

  return (
    <Router>
      <GlobalNoindex />
      <div className="app">
        <BetaBanner />
        <Header isDark={isDark} onToggleTheme={toggleTheme} />
        <Routes>
          <Route path="/" element={<RootRoute isDark={isDark} onToggleTheme={toggleTheme} />} />
          {/* Лендинг переехал в корень; старые ссылки на /login продолжают работать */}
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="/connect" element={<InstagramFullConnect />} />
          <Route path="/account" element={<AccountStatusPage />} />
          <Route path="/settings" element={<AccountSettingsPage />} />
          <Route path="/report" element={<ReportPage key="my-report" />} />
          <Route path="/report/:username" element={<ReportPage key="public-report" isPublic />} />
          <Route path="/catalog" element={<CatalogPage />} />
          {/* Meta OAuth callback (старый фронтенд callback) */}
          <Route path="/auth/callback" element={<AuthCallback method="meta" />} />
          {/* Backend redirect uris - они перенаправляют обратно на фронтенд */}
          <Route path="/api/auth/meta/callback" element={<AuthCallback method="meta" />} />
          <Route path="/api/auth/instagram/callback" element={<AuthCallback method="instagram" />} />
          {/* Blog */}
          <Route path="/blog" element={<BlogIndexPage />} />
          <Route path="/blog/:slug" element={<BlogArticlePage />} />
          {/* Legal pages */}
          <Route path="/privacy" element={<><SeoHead title="Политика конфиденциальности" description="Как Trend собирает, использует и защищает данные блогеров и рекламодателей." canonical="/privacy" /><MarkdownPage contentPath={(lang) => import(`../content/legal/privacy.${lang}.md`)} /></>} />
          <Route path="/terms" element={<><SeoHead title="Пользовательское соглашение" description="Условия использования сервиса Trend — аналитика Instagram для блогеров и рекламодателей." canonical="/terms" /><MarkdownPage contentPath={(lang) => import(`../content/legal/terms.${lang}.md`)} /></>} />
          <Route path="/data-deletion" element={<><SeoHead title="Удаление данных" description="Как удалить свои данные из Trend. Инструкция по отзыву доступа и удалению аккаунта." canonical="/data-deletion" /><MarkdownPage contentPath={(lang) => import(`../content/legal/deletion.${lang}.md`)} /></>} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>
    </Router>
  );
};

// Прямой переход на Instagram-авторизацию с расширенным набором разрешений
// (публикация, сообщения, комментарии). Отдельная ссылка для блогеров —
// лендинг и его кнопка «Войти» используют базовый набор разрешений.
const InstagramFullConnect: React.FC = () => {
  React.useEffect(() => {
    const state = Math.random().toString(36).substring(7);
    localStorage.setItem("oauth_state", state);
    localStorage.setItem("auth_method", "instagram");
    window.location.href = `${API_BASE_URL}/api/auth/instagram/start?state=${state}&full=1`;
  }, []);
  return null;
};

interface AuthCallbackProps {
  method?: "meta" | "instagram";
}

const AuthCallback: React.FC<AuthCallbackProps> = ({ method: defaultMethod = "meta" }) => {
  const [error, setError] = React.useState<string | null>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const state = params.get("state");
    const method = params.get("method") || defaultMethod;
    const storedState = localStorage.getItem("oauth_state");

    // Бэкенд уже обменял code → token и прислал access_token (JWT) в redirect
    const accessToken = params.get("access_token");
    const errorParam = params.get("error");

    if (errorParam) {
      setError(decodeURIComponent(errorParam));
      return;
    }

    if (accessToken) {
      if (state && storedState && state !== storedState) {
        setError(t("auth.csrfError"));
        localStorage.removeItem("oauth_state");
        return;
      }
      localStorage.setItem("access_token", accessToken);
      localStorage.setItem("auth_method", method);
      localStorage.removeItem("oauth_state");
      analytics.loginSuccess(method);
      console.log(`✅ Authenticated via ${method}, JWT stored`);
      // SPA-переход вместо window.location.href: не перезагружаем бандл заново
      navigate("/report", { replace: true });
      return;
    }

    // Fallback: старый flow — code в URL, отправляем на бэкенд POST
    const code = params.get("code");
    if (!code || !state || state !== storedState) {
      setError("Invalid authentication response");
      return;
    }

    const authMethod = localStorage.getItem("auth_method") || defaultMethod;
    const doCallback = async () => {
      try {
        const authResp = authMethod === "instagram"
          ? await apiClient.instagramDirectOAuthCallback(code, state)
          : await apiClient.metaOAuthCallback(code, state);
        localStorage.setItem("access_token", authResp.access_token);
        localStorage.setItem("auth_method", authMethod);
        localStorage.removeItem("oauth_state");
        analytics.loginSuccess(authMethod);
        navigate("/report", { replace: true });
      } catch (err: any) {
        setError(err.response?.data?.detail || err.message || "Authentication failed");
      }
    };
    doCallback();
  }, [defaultMethod, t, navigate]);

  if (error) {
    return (
      <div className="auth-callback auth-callback--error">
        <h2>{t("auth.error")}</h2>
        <p>{error}</p>
        <button className="btn-secondary" onClick={() => window.location.href = "/"}>{t("auth.returnToLogin")}</button>
      </div>
    );
  }

  return (
    <div className="auth-callback">
      <p>{t("auth.processing")}</p>
    </div>
  );
};
